import { useMemo } from 'react'
import { format, startOfDay, endOfDay, eachDayOfInterval } from 'date-fns'
import type { RawContact, RawMessage, OverviewAnalytics, DailyTrend, FlagBreakdown, FlagBreakdownContact } from '../components/types'
import { DateRange } from 'react-day-picker'
import { getSlaHoursForLevel, computeResponseMetrics, computeMedian, computeModeratorResponseTimes, computeEffectiveResponseTimes, aggregateResponseTimes, type BusinessHoursConfig } from '../utils/analyticsUtils'
import type { DashboardContactSourceFilter } from './useSupabaseDashboardData'

// Re-export types for convenience
export type { OverviewAnalytics, DailyTrend, FlagBreakdown, FlagBreakdownContact }

export interface HourlyData {
  hour: number
  label: string
  incoming: number
  outgoing: number
}

export interface DashboardCommentItem {
  contactId: string
  contact: RawContact
  messageId?: string
  associatedMessage?: string
  commentText: string
  commenterName: string
  commenterId: string
  timestamp: number
  type: 'message' | 'conversation'
}

function parseIdList(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value
    .map((v) => String(v || "").trim())
    .filter(Boolean)
}

function parseCategoryList(vars: Record<string, any>): string[] {
  const categories = parseIdList(vars.risk_categories)
  if (categories.length > 0) return categories
  if (vars.risk_category_name) return [String(vars.risk_category_name)]
  return []
}

function friendlyFlagName(flagId: string): string {
  const normalized = flagId.toLowerCase()
  if (normalized === "high_risk") return "High Risk"
  if (normalized === "low_confidence") return "Low Confidence"
  if (normalized === "asked_human") return "Asked Human"
  return flagId
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (ch) => ch.toUpperCase())
}

export function useAnalytics(
  contacts: RawContact[],
  messages: RawMessage[],
  dateRange: DateRange,
  contactSourceFilter: DashboardContactSourceFilter = "real_only",
  businessHours?: BusinessHoursConfig
) {
  // Filter by date range
  const { filteredContacts, filteredMessages } = useMemo(() => {
    if (!dateRange.from || !dateRange.to) {
      return { filteredContacts: contacts, filteredMessages: messages }
    }
    const fromTs = startOfDay(dateRange.from).getTime() / 1000
    const toTs = endOfDay(dateRange.to).getTime() / 1000
    return {
      // Contacts should not be dropped by contact creation date. Older contacts
      // can still have in-range messages/comments we need to show/export.
      filteredContacts: contacts,
      filteredMessages: messages.filter(m => m.time_created >= fromTs && m.time_created <= toTs)
    }
  }, [contacts, messages, dateRange])

  // Overview Analytics
  const overviewAnalytics = useMemo((): OverviewAnalytics => {
    const totalMessages = filteredMessages.length
    const incomingMessages = filteredMessages.filter(m => m.direction === 'incoming').length
    const outgoingMessages = filteredMessages.filter(m => m.direction === 'outgoing').length

    // Human vs AI generated responses (outgoing only).
    // A response is human-generated when it has a `sender` (operator contact) or
    // was written via the HITL flow (role === "human"); everything else outgoing is AI.
    const outgoing = filteredMessages.filter(m => m.direction === 'outgoing')
    const isHumanResponse = (m: RawMessage) => m.sender != null || m.role === 'human'
    const humanResponses = outgoing.filter(isHumanResponse).length
    const aiResponses = outgoing.length - humanResponses

    // Human takeovers = distinct conversations where a human operator sent at
    // least one response (a HITL message). Answers "how many conversations
    // required takeover", vs humanResponses which counts individual messages.
    const takeoverContactIds = new Set<string>()
    outgoing.forEach(m => { if (isHumanResponse(m) && m.contact_id) takeoverContactIds.add(m.contact_id) })
    const humanTakeovers = takeoverContactIds.size

    // Active users = unique contacts with messages in the period
    const activeContactIds = new Set(filteredMessages.map(m => m.contact_id))
    const activeUsers = activeContactIds.size
    const totalContacts = activeUsers
    
    // New users in period (contacts created in date range)
    const newUsers = contacts.filter(c => {
      if (!dateRange.from || !dateRange.to) return false
      const createdTs = c.time_created * 1000
      return createdTs >= startOfDay(dateRange.from).getTime() && createdTs <= endOfDay(dateRange.to).getTime()
    }).length

    // Handled by human vs bot (from contacts who were active in period)
    const activeContacts = contacts.filter(c => activeContactIds.has(c.id))
    const humanHandled = activeContacts.filter(c => c.vars?.handled_by === 'human').length
    const botHandled = activeContacts.length - humanHandled

    // Average messages per user
    const avgMessagesPerUser = activeUsers > 0 ? (totalMessages / activeUsers).toFixed(1) : '0'

    // Response rate and latencies
    const messagesByContact = new Map<string, RawMessage[]>()
    filteredMessages.forEach(m => {
      if (!messagesByContact.has(m.contact_id)) messagesByContact.set(m.contact_id, [])
      messagesByContact.get(m.contact_id)!.push(m)
    })

    const { latencies, incomingWithResponse, totalIncoming } = computeResponseMetrics(messagesByContact)

    const responseRate = totalIncoming > 0 ? ((incomingWithResponse / totalIncoming) * 100).toFixed(1) : '0'
    const medianResponseTime = computeMedian(latencies)

    // How long users wait on a human. Measured only for moderator replies, from
    // the last user message, counting the first message of each moderator run.
    const moderatorResponseTimes = computeModeratorResponseTimes(messagesByContact, businessHours)
    const medianModeratorResponseTime = aggregateResponseTimes(
      moderatorResponseTimes,
      businessHours?.aggregation ?? "median"
    )
    const moderatorResponseCount = moderatorResponseTimes.length

    // Effective response time: what the user actually waited for a real answer —
    // the human's reply where one came, the bot's where it handled the message.
    const effective = computeEffectiveResponseTimes(messagesByContact, businessHours)
    const how = businessHours?.aggregation ?? "median"
    const effectiveResponseTime = aggregateResponseTimes(effective.all, how)
    const effectiveResponseCount = effective.all.length
    const effectiveHumanCount = effective.human.length
    const effectiveBotCount = effective.bot.length

    // Ratings (thumbs up/down)
    const ratedMessages = filteredMessages.filter(m => 
      m.vars?.rating !== undefined && m.vars?.rating !== null && m.vars?.rating !== ''
    )
    const thumbsUp = ratedMessages.filter(m => m.vars?.rating === '1' || m.vars?.rating === 1).length
    const thumbsDown = ratedMessages.filter(m => m.vars?.rating === '0' || m.vars?.rating === 0).length
    const thumbsUpRate = ratedMessages.length > 0 ? ((thumbsUp / ratedMessages.length) * 100).toFixed(0) : 'N/A'
    const correctedResponses = filteredMessages.filter(m => m.vars?.rating_example).length

    return {
      totalContacts,
      totalMessages,
      incomingMessages,
      outgoingMessages,
      aiResponses,
      humanResponses,
      activeUsers,
      newUsers,
      humanHandled,
      botHandled,
      humanTakeovers,
      avgMessagesPerUser,
      responseRate,
      medianResponseTime,
      medianModeratorResponseTime,
      moderatorResponseCount,
      effectiveResponseTime,
      effectiveResponseCount,
      effectiveHumanCount,
      effectiveBotCount,
      thumbsUp,
      thumbsDown,
      thumbsUpRate,
      ratedCount: ratedMessages.length,
      correctedResponses
    }
  }, [filteredContacts, filteredMessages, contacts, dateRange, businessHours])

  // Daily trends
  const dailyTrends = useMemo((): DailyTrend[] => {
    if (!dateRange.from || !dateRange.to) return []
    
    const days = eachDayOfInterval({ start: dateRange.from, end: dateRange.to })
    let cumulativeUsers = 0
    let cumulativeIncoming = 0
    let cumulativeOutgoing = 0
    
    return days.map(day => {
      const dayStart = startOfDay(day).getTime() / 1000
      const dayEnd = endOfDay(day).getTime() / 1000
      
      const dayMessages = filteredMessages.filter(m => m.time_created >= dayStart && m.time_created <= dayEnd)
      const dayNewUsers = contacts.filter(c => c.time_created >= dayStart && c.time_created <= dayEnd)
      const dayActiveUsers = new Set(dayMessages.map(m => m.contact_id)).size

      const dayIncoming = dayMessages.filter(m => m.direction === 'incoming').length
      const dayOutgoing = dayMessages.filter(m => m.direction === 'outgoing').length

      const dayRated = dayMessages.filter(m =>
        m.vars?.rating !== undefined && m.vars?.rating !== null && m.vars?.rating !== ''
      )
      const dayThumbsUp = dayRated.filter(m => m.vars?.rating === '1' || m.vars?.rating === 1).length
      const dayFeedbackRate = dayRated.length > 0 ? Math.round((dayThumbsUp / dayRated.length) * 100) : null

      cumulativeUsers += dayNewUsers.length
      cumulativeIncoming += dayIncoming
      cumulativeOutgoing += dayOutgoing

      return {
        date: format(day, 'MMM d'),
        incoming: dayIncoming,
        outgoing: dayOutgoing,
        cumulativeIncoming,
        cumulativeOutgoing,
        newUsers: dayNewUsers.length,
        activeUsers: dayActiveUsers,
        cumulativeUsers,
        feedbackRate: dayFeedbackRate,
        ratedCount: dayRated.length,
      }
    })
  }, [filteredMessages, contacts, dateRange])

  // Hourly distribution
  const hourlyData = useMemo((): HourlyData[] => {
    const hours = Array.from({ length: 24 }, (_, i) => ({
      hour: i,
      label: `${i.toString().padStart(2, '0')}:00`,
      incoming: 0,
      outgoing: 0
    }))

    filteredMessages.forEach(m => {
      const hour = new Date(m.time_created * 1000).getHours()
      if (m.direction === 'incoming') {
        hours[hour].incoming++
      } else {
        hours[hour].outgoing++
      }
    })

    return hours
  }, [filteredMessages])

  // Escalation breakdown from the current flag model.
  const flagBreakdown = useMemo((): FlagBreakdown[] => {
    const contactLookup = new Map(filteredContacts.map(c => [c.id, c]))
    const makeContact = (contactId: string, status: 'active' | 'resolved'): FlagBreakdownContact => {
      const c = contactLookup.get(contactId)
      return { contactId, phoneNumber: c?.phone_number, name: c?.name, status }
    }

    type ContactSnapshot = {
      activeFlags: Set<string>
      resolvedFlags: Set<string>
      categories: Set<string>
      severity: number | null
    }

    const byContact = new Map<string, ContactSnapshot>()
    filteredContacts.forEach((c) => {
      byContact.set(c.id, {
        activeFlags: new Set<string>(),
        resolvedFlags: new Set<string>(),
        categories: new Set<string>(),
        severity: c.severity ?? null,
      })
    })

    filteredMessages.forEach((m) => {
      if (m.direction !== 'incoming') return
      const snapshot = byContact.get(m.contact_id)
      if (!snapshot) return
      const vars = m.vars || {}

      const activeIds = new Set<string>(parseIdList(vars.active_flag_ids))
      const resolvedIds = new Set<string>(parseIdList(vars.resolved_flag_ids))

      if (vars.high_risk_flag === 'flagged') activeIds.add('high_risk')
      if (vars.low_confidence_flag === 'flagged') activeIds.add('low_confidence')
      if (vars.asked_human_flag === 'flagged') activeIds.add('asked_human')
      if (vars.high_risk_flag === 'resolved') resolvedIds.add('high_risk')
      if (vars.low_confidence_flag === 'resolved') resolvedIds.add('low_confidence')
      if (vars.asked_human_flag === 'resolved') resolvedIds.add('asked_human')

      activeIds.forEach((id) => snapshot.activeFlags.add(id))
      resolvedIds.forEach((id) => snapshot.resolvedFlags.add(id))
      parseCategoryList(vars).forEach((cat) => snapshot.categories.add(cat))
    })

    const flagAgg = new Map<string, { active: Set<string>; resolved: Set<string> }>()
    const categoryAgg = new Map<string, { active: Set<string>; resolved: Set<string> }>()
    const severityAgg = new Map<string, { active: Set<string>; resolved: Set<string>; level: number }>()

    byContact.forEach((snapshot, contactId) => {
      const hasActive = snapshot.activeFlags.size > 0
      const hasResolvedOnly = !hasActive && snapshot.resolvedFlags.size > 0
      if (!hasActive && !hasResolvedOnly) return

      snapshot.activeFlags.forEach((flagId) => {
        if (!flagAgg.has(flagId)) flagAgg.set(flagId, { active: new Set(), resolved: new Set() })
        flagAgg.get(flagId)!.active.add(contactId)
      })

      snapshot.resolvedFlags.forEach((flagId) => {
        if (snapshot.activeFlags.has(flagId)) return
        if (!flagAgg.has(flagId)) flagAgg.set(flagId, { active: new Set(), resolved: new Set() })
        flagAgg.get(flagId)!.resolved.add(contactId)
      })

      snapshot.categories.forEach((category) => {
        if (!categoryAgg.has(category)) categoryAgg.set(category, { active: new Set(), resolved: new Set() })
        const target = hasActive ? categoryAgg.get(category)!.active : categoryAgg.get(category)!.resolved
        target.add(contactId)
      })

      if (snapshot.severity != null) {
        const level = Math.round(snapshot.severity)
        const key = `Severity ${level}`
        if (!severityAgg.has(key)) severityAgg.set(key, { active: new Set(), resolved: new Set(), level })
        const target = hasActive ? severityAgg.get(key)!.active : severityAgg.get(key)!.resolved
        target.add(contactId)
      }
    })

    const flagColors = ['#ef4444', '#f59e0b', '#3b82f6', '#a855f7', '#14b8a6', '#f97316', '#22c55e']
    const categoryColors = ['#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#eab308']
    const severityColor = '#9333ea'

    const toBreakdown = (
      name: string,
      kind: "flag" | "category" | "severity",
      color: string,
      activeIds: Set<string>,
      resolvedIds: Set<string>
    ): FlagBreakdown => {
      const activeContacts = Array.from(activeIds).map((id) => makeContact(id, 'active'))
      const resolvedContacts = Array.from(resolvedIds).map((id) => makeContact(id, 'resolved'))
      return {
        name,
        kind,
        active: activeContacts.length,
        resolved: resolvedContacts.length,
        total: activeContacts.length + resolvedContacts.length,
        color,
        contacts: [...activeContacts, ...resolvedContacts],
      }
    }

    const flagRows = Array.from(flagAgg.entries())
      .sort((a, b) => (b[1].active.size + b[1].resolved.size) - (a[1].active.size + a[1].resolved.size))
      .map(([flagId, counts], i) =>
        toBreakdown(friendlyFlagName(flagId), "flag", flagColors[i % flagColors.length], counts.active, counts.resolved)
      )

    const categoryRows = Array.from(categoryAgg.entries())
      .sort((a, b) => (b[1].active.size + b[1].resolved.size) - (a[1].active.size + a[1].resolved.size))
      .map(([category, counts], i) =>
        toBreakdown(category, "category", categoryColors[i % categoryColors.length], counts.active, counts.resolved)
      )

    const severityRows = Array.from(severityAgg.entries())
      .sort((a, b) => b[1].level - a[1].level)
      .map(([label, counts]) => toBreakdown(label, "severity", severityColor, counts.active, counts.resolved))

    return [...flagRows, ...categoryRows, ...severityRows].filter((f) => (f.total || 0) > 0)
  }, [filteredMessages, filteredContacts])

  // Rating distribution
  const ratingDistribution = useMemo(() => {
    let thumbsUp = 0
    let thumbsDown = 0
    
    filteredMessages.forEach(m => {
      if (m.vars?.rating !== undefined && m.vars?.rating !== null && m.vars?.rating !== '') {
        if (m.vars.rating === '1' || m.vars.rating === 1) {
          thumbsUp++
        } else if (m.vars.rating === '0' || m.vars.rating === 0) {
          thumbsDown++
        }
      }
    })

    return { thumbsUp, thumbsDown, total: thumbsUp + thumbsDown }
  }, [filteredMessages])

  // Corrected responses list
  const correctedResponsesList = useMemo(() => {
    return filteredMessages
      .filter(m => m.vars?.rating_example)
      .map(m => ({
        messageId: m.id,
        contactId: m.contact_id,
        content: m.content,
        correctedResponse: m.vars?.rating_example,
        timestamp: m.time_created,
        contact: contacts.find(c => c.id === m.contact_id)
      }))
      .sort((a, b) => b.timestamp - a.timestamp)
  }, [filteredMessages, contacts])

  // Conversation status breakdown
  const statusBreakdown = useMemo(() => {
    const statuses: Record<string, number> = {}
    filteredContacts.forEach(c => {
      const status = c.conversation_status || 'unknown'
      statuses[status] = (statuses[status] || 0) + 1
    })
    return Object.entries(statuses).map(([status, count]) => ({
      status: status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' '),
      count,
      percentage: filteredContacts.length > 0 ? ((count / filteredContacts.length) * 100).toFixed(1) : '0'
    }))
  }, [filteredContacts])

  // Extract all unique variable keys from contacts and messages (exclude internal_comments)
  const INTERNAL_COMMENTS_KEY = 'internal_comments'
  const EXTRACTIONS_KEY = 'extractions'
  const ANALYSIS_EXTRACTIONS_KEY = 'analysis_extractions'

  const addExtractionLeafKeys = (value: unknown, prefix: string, target: Set<string>) => {
    if (value === undefined) return
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
      target.add(`extractions.${prefix}`)
      return
    }

    for (const [childKey, childValue] of Object.entries(value as Record<string, unknown>)) {
      const nextPrefix = prefix ? `${prefix}.${childKey}` : childKey
      addExtractionLeafKeys(childValue, nextPrefix, target)
    }
  }

  const addVariableKeys = (vars: Record<string, any> | undefined, target: Set<string>) => {
    if (!vars) return
    for (const [key, value] of Object.entries(vars)) {
      if (key === INTERNAL_COMMENTS_KEY) continue
      if (key === EXTRACTIONS_KEY || key === ANALYSIS_EXTRACTIONS_KEY) {
        const extractionRoot = value && typeof value === 'object' ? value : {}
        for (const payload of Object.values(extractionRoot)) {
          const data = payload && typeof payload === 'object'
            ? ((payload as any).extracted_data && typeof (payload as any).extracted_data === 'object'
              ? (payload as any).extracted_data
              : payload)
            : {}
          Object.entries(data || {}).forEach(([fieldKey, fieldValue]) => addExtractionLeafKeys(fieldValue, fieldKey, target))
        }
        continue
      }
      if (value && typeof value === 'object' && !Array.isArray(value)) continue
      target.add(key)
    }
  }

  const allVarKeys = useMemo(() => {
    const contactVars = new Set<string>()
    const messageVars = new Set<string>()

    filteredContacts.forEach(c => {
      addVariableKeys(c.vars, contactVars)
    })
    filteredMessages.forEach(m => {
      addVariableKeys(m.vars, messageVars)
    })

    // Form fields are namespaced "form.<field>" when flattened onto contacts;
    // surface them as their own group rather than mixed in with contact vars.
    const formVars = new Set<string>()
    for (const key of contactVars) {
      if (key.startsWith("form.")) formVars.add(key)
    }
    for (const key of formVars) contactVars.delete(key)

    return {
      contact: Array.from(contactVars).sort(),
      message: Array.from(messageVars).sort(),
      form: Array.from(formVars).sort(),
    }
  }, [filteredContacts, filteredMessages])

  // SLA metrics
  const slaMetrics = useMemo(() => {
    const now = Date.now() / 1000
    const contactLookup = new Map(filteredContacts.map(c => [c.id, c]))
    const makeContact = (contactId: string, status: 'active' | 'resolved'): FlagBreakdownContact => {
      const c = contactLookup.get(contactId)
      return { contactId, phoneNumber: c?.phone_number, name: c?.name, status }
    }

    // Track per-contact snapshots for active escalations.
    const snapshotByContact = new Map<string, { hasActive: boolean; earliestIncomingTs: number | null }>()

    filteredMessages.forEach(m => {
      if (m.direction !== 'incoming') return
      const vars = m.vars || {}
      const active = parseIdList(vars.active_flag_ids)
      const hasActive =
        active.length > 0 ||
        vars.high_risk_flag === "flagged" ||
        vars.low_confidence_flag === "flagged" ||
        vars.asked_human_flag === "flagged"

      if (!snapshotByContact.has(m.contact_id)) {
        snapshotByContact.set(m.contact_id, { hasActive: false, earliestIncomingTs: m.time_created })
      }
      const snapshot = snapshotByContact.get(m.contact_id)!
      if (hasActive) snapshot.hasActive = true
      if (snapshot.earliestIncomingTs == null || m.time_created < snapshot.earliestIncomingTs) {
        snapshot.earliestIncomingTs = m.time_created
      }
    })

    const contactSlas = new Map<string, { deadline: number; contact: FlagBreakdownContact }>()
    snapshotByContact.forEach((snapshot, contactId) => {
      if (!snapshot.hasActive) return
      const severity = contactLookup.get(contactId)?.severity
      if (severity == null) return
      const slaHours = getSlaHoursForLevel(String(severity))
      if (slaHours === null) return
      const baseTs = snapshot.earliestIncomingTs ?? now
      contactSlas.set(contactId, { deadline: baseTs + (slaHours * 3600), contact: makeContact(contactId, "active") })
    })

    const totalWithSla = contactSlas.size
    const totalContacts = filteredContacts.length

    let breached = 0
    let within1h = 0
    let within4h = 0
    let within24h = 0
    let within72h = 0
    let beyond72h = 0

    const breachedContacts: FlagBreakdownContact[] = []
    const within1hContacts: FlagBreakdownContact[] = []
    const within4hContacts: FlagBreakdownContact[] = []
    const within24hContacts: FlagBreakdownContact[] = []
    const within72hContacts: FlagBreakdownContact[] = []
    const beyond72hContacts: FlagBreakdownContact[] = []

    contactSlas.forEach(({ deadline, contact }) => {
      const remaining = deadline - now
      if (remaining <= 0) {
        breached++
        breachedContacts.push(contact)
      } else if (remaining <= 3600) {
        within1h++
        within1hContacts.push(contact)
      } else if (remaining <= 4 * 3600) {
        within4h++
        within4hContacts.push(contact)
      } else if (remaining <= 24 * 3600) {
        within24h++
        within24hContacts.push(contact)
      } else if (remaining <= 72 * 3600) {
        within72h++
        within72hContacts.push(contact)
      } else {
        beyond72h++
        beyond72hContacts.push(contact)
      }
    })

    return {
      totalContacts,
      totalWithSla,
      percentWithSla: totalContacts > 0 ? (totalWithSla / totalContacts) * 100 : 0,
      breached,
      percentBreached: totalWithSla > 0 ? (breached / totalWithSla) * 100 : 0,
      buckets: [
        { label: "Breached", count: breached, color: "#ef4444", contacts: breachedContacts },
        { label: "< 1 hour", count: within1h, color: "#f97316", contacts: within1hContacts },
        { label: "1-4 hours", count: within4h, color: "#f59e0b", contacts: within4hContacts },
        { label: "4-24 hours", count: within24h, color: "#eab308", contacts: within24hContacts },
        { label: "1-3 days", count: within72h, color: "#84cc16", contacts: within72hContacts },
        { label: "> 3 days", count: beyond72h, color: "#10b981", contacts: beyond72hContacts },
      ],
    }
  }, [filteredMessages, filteredContacts])

  // Escalation rates by category (% of escalated conversations).
  const escalationRates = useMemo(() => {
    const byContact = new Map<string, { hasActive: boolean; hasResolved: boolean; categories: Set<string> }>()
    filteredMessages.forEach((m) => {
      if (m.direction !== 'incoming') return
      if (!byContact.has(m.contact_id)) byContact.set(m.contact_id, { hasActive: false, hasResolved: false, categories: new Set<string>() })
      const vars = m.vars || {}
      const active = parseIdList(vars.active_flag_ids)
      const resolved = parseIdList(vars.resolved_flag_ids)
      const state = byContact.get(m.contact_id)!

      if (active.length > 0 || vars.high_risk_flag === "flagged" || vars.low_confidence_flag === "flagged" || vars.asked_human_flag === "flagged") {
        state.hasActive = true
      }
      if (resolved.length > 0 || vars.high_risk_flag === "resolved" || vars.low_confidence_flag === "resolved" || vars.asked_human_flag === "resolved") {
        state.hasResolved = true
      }
      parseCategoryList(vars).forEach((cat) => state.categories.add(cat))
    })

    const escalatedContacts = Array.from(byContact.values()).filter((c) => c.hasActive || c.hasResolved)
    const denominator = escalatedContacts.length
    if (denominator === 0) return []

    const categoryCounts: Record<string, number> = {}
    escalatedContacts.forEach((c) => {
      c.categories.forEach((cat) => {
        categoryCounts[cat] = (categoryCounts[cat] || 0) + 1
      })
    })

    return Object.entries(categoryCounts)
      .map(([name, count]) => ({
        name,
        count,
        percentage: (count / denominator) * 100,
      }))
      .sort((a, b) => b.count - a.count)
  }, [filteredMessages])

  // Escalation aggregates by conversation (not flag instance).
  const escalationAggregates = useMemo(() => {
    const contactLookup = new Map(filteredContacts.map(c => [c.id, c]))
    const makeContact = (contactId: string, status: 'active' | 'resolved'): FlagBreakdownContact => {
      const c = contactLookup.get(contactId)
      return { contactId, phoneNumber: c?.phone_number, name: c?.name, status }
    }

    const byContact = new Map<string, { hasActive: boolean; hasResolved: boolean }>()
    filteredMessages.forEach((m) => {
      if (m.direction !== "incoming") return
      if (!byContact.has(m.contact_id)) byContact.set(m.contact_id, { hasActive: false, hasResolved: false })
      const state = byContact.get(m.contact_id)!
      const vars = m.vars || {}
      const active = parseIdList(vars.active_flag_ids)
      const resolved = parseIdList(vars.resolved_flag_ids)
      if (active.length > 0 || vars.high_risk_flag === "flagged" || vars.low_confidence_flag === "flagged" || vars.asked_human_flag === "flagged") {
        state.hasActive = true
      }
      if (resolved.length > 0 || vars.high_risk_flag === "resolved" || vars.low_confidence_flag === "resolved" || vars.asked_human_flag === "resolved") {
        state.hasResolved = true
      }
    })

    const activeIds: string[] = []
    const resolvedIds: string[] = []
    const allEscalatedIds: string[] = []
    const anyResolvedIds: string[] = []

    byContact.forEach((state, contactId) => {
      if (!state.hasActive && !state.hasResolved) return
      allEscalatedIds.push(contactId)
      if (state.hasResolved) anyResolvedIds.push(contactId)
      if (state.hasActive) activeIds.push(contactId)
      else resolvedIds.push(contactId)
    })

    const activeContacts = activeIds.map((id) => makeContact(id, "active"))
    const resolvedContacts = resolvedIds.map((id) => makeContact(id, "resolved"))
    const totalEscalatedContacts = allEscalatedIds.map((id) => makeContact(id, activeIds.includes(id) ? "active" : "resolved"))
    const convosWithResolvedFlagContacts = anyResolvedIds.map((id) => makeContact(id, activeIds.includes(id) ? "active" : "resolved"))

    // Needs reply: last message from contact is incoming and no flag has been dismissed
    const lastMsgByContact = new Map<string, { direction: string; time: number }>()
    filteredMessages.forEach(m => {
      const existing = lastMsgByContact.get(m.contact_id)
      if (!existing || m.time_created > existing.time) {
        lastMsgByContact.set(m.contact_id, { direction: m.direction, time: m.time_created })
      }
    })
    const needsReplyContacts: FlagBreakdownContact[] = []
    activeIds.forEach(id => {
      const last = lastMsgByContact.get(id)
      if (last?.direction === 'incoming') {
        needsReplyContacts.push(makeContact(id, 'active'))
      }
    })

    return {
      activeTickets: activeContacts.length,
      resolvedTickets: resolvedContacts.length,
      activeContacts,
      resolvedContacts,
      totalEscalatedContacts,
      convosWithResolvedFlagContacts,
      needsReplyCount: needsReplyContacts.length,
      needsReplyContacts,
    }
  }, [filteredContacts, filteredMessages])

  // Flatten comments from contact vars (internal_comments) for Messages tab and export
  const commentsList = useMemo((): DashboardCommentItem[] => {
    const out: DashboardCommentItem[] = []
    const messageContentById = new Map<string, string>()
    messages.forEach((message) => {
      const id = String(message.id || "").trim()
      if (!id) return
      messageContentById.set(id, message.content ?? "")
    })

    filteredContacts.forEach(contact => {
      // A contact can land in `filteredContacts` purely because it holds form
      // data — that path intentionally bypasses the real/synthetic query
      // filter (see RawContact.type) so simulation-captured forms still show in
      // the Variables tab. Comments don't get that exception: when the user
      // has "real conversations only" selected, a comment left on a simulated
      // conversation should not surface just because the same contact also
      // happens to carry form data.
      if (contactSourceFilter === "real_only" && contact.type === "synthetic") return
      if (contactSourceFilter === "synthetic_only" && contact.type !== "synthetic") return

      const raw = contact?.vars?.[INTERNAL_COMMENTS_KEY]
      if (!raw) return
      const parsed = typeof raw === 'string' ? (() => { try { return JSON.parse(raw) } catch { return null } })() : raw
      const arr = Array.isArray(parsed) ? parsed : []
      arr.forEach((entry: any) => {
        const text = entry?.text ?? entry?.comment ?? ''
        const commenterName = entry?.author_name ?? entry?.commenter ?? ''
        const commenterId = String(entry?.author_id ?? entry?.commenter_id ?? '')
        const timestamp = Number(entry?.created_at ?? entry?.timestamp ?? 0)
        const messageId = entry?.message_id ? String(entry.message_id).trim() : undefined
        const associatedMessage = messageId ? (messageContentById.get(messageId) ?? '') : ''
        if (!text && !commenterName) return
        out.push({
          contactId: contact.id,
          contact,
          messageId,
          associatedMessage,
          commentText: text,
          commenterName,
          commenterId,
          timestamp,
          type: messageId ? 'message' : 'conversation'
        })
      })
    })
    return out.sort((a, b) => b.timestamp - a.timestamp)
  }, [filteredContacts, messages, contactSourceFilter])

  return {
    filteredContacts,
    filteredMessages,
    overviewAnalytics,
    dailyTrends,
    hourlyData,
    flagBreakdown,
    ...escalationAggregates,
    escalationRates,
    slaMetrics,
    ratingDistribution,
    correctedResponsesList,
    commentsList,
    statusBreakdown,
    allVarKeys,
  }
}
